<template>
  <div class="w-full flex flex-col gap-4 md:gap-4 h-full min-h-0">
    <!-- Desktop header: legends + buttons side by side -->
    <div class="hidden md:flex flex-row justify-between gap-2">
      <legends-group />
      <div ref="exportMenuContainerDesktop" class="relative flex flex-row gap-4">
        <filled-button
          v-if="canCreateStation"
          @click="showCreateStationModal = true"
          text="Criar plantão"
          class="bg-primary30"
          width="150px"
          height="48px"
        />
        <outline-button
          v-if="canStationDoctorCheckXlsReportExporting"
          @click="toggleExportMenu"
          density="compact"
          width="120px"
          height="48px"
        >
          <v-icon :icon="mdiChevronDown" :size="24" class="cursor-pointer" />Exportar
        </outline-button>
        <div
          v-if="showExportMenu"
          class="absolute right-0 top-full mt-2 w-[320px] origin-top-right rounded-md bg-white shadow-lg focus:outline-none z-30 border border-gray-200"
        >
          <div class="py-1">
            <a
              v-if="canStationDoctorCheckXlsReportExporting"
              class="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary20 hover:text-white border-b border-gray-200"
              @click="downloadExcel"
            >
              <excelIcon width="20" height="20" color="currentColor" />
              <span>Exportar relatório de apontamentos</span>
            </a>
            <a
              v-if="canStationPdfReportExporting"
              class="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary20 hover:text-white border-b border-gray-200"
              @click="downloadSchedules"
            >
              <pdfIcon width="20" height="20" color="currentColor" />
              <span>Baixar escala em .pdf</span>
            </a>
            <a
              v-if="canStationXlsReportExporting"
              class="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary20 hover:text-white"
              @click="downloadStationsExcel"
            >
              <excelIcon width="20" height="20" color="currentColor" />
              <span>Exportar relatório de vagas</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile header: compact toolbar -->
    <div class="md:hidden">
      <div class="flex items-center gap-2">
        <filled-button
          v-if="canCreateStation"
          @click="showCreateStationModal = true"
          text="+ Plantão"
          class="bg-primary30"
          width="auto"
          height="36px"
        />
        <div ref="exportMenuContainerMobile" class="relative">
          <outline-button
            v-if="canStationDoctorCheckXlsReportExporting"
            @click="toggleExportMenu"
            density="compact"
            width="auto"
            height="36px"
          >
            <v-icon :icon="mdiChevronDown" :size="18" class="cursor-pointer" />Exportar
          </outline-button>
          <div
            v-if="showExportMenu"
            class="absolute right-0 top-full mt-2 w-[280px] origin-top-right rounded-md bg-white shadow-lg focus:outline-none z-30 border border-gray-200"
          >
            <div class="py-1">
              <a
                v-if="canStationDoctorCheckXlsReportExporting"
                class="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary20 hover:text-white border-b border-gray-200"
                @click="downloadExcel"
              >
                <excelIcon width="20" height="20" color="currentColor" />
                <span>Exportar apontamentos</span>
              </a>
              <a
                v-if="canStationPdfReportExporting"
                class="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary20 hover:text-white border-b border-gray-200"
                @click="downloadSchedules"
              >
                <pdfIcon width="20" height="20" color="currentColor" />
                <span>Baixar escala em .pdf</span>
              </a>
              <a
                v-if="canStationXlsReportExporting"
                class="flex cursor-pointer items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary20 hover:text-white"
                @click="downloadStationsExcel"
              >
                <excelIcon width="20" height="20" color="currentColor" />
                <span>Exportar relatório de vagas</span>
              </a>
            </div>
          </div>
        </div>
        <button
          @click="showMobileFilters = !showMobileFilters"
          class="flex items-center gap-1 px-3 h-[36px] border border-gray-300 rounded-full text-gray-500 text-sm"
        >
          <v-icon :icon="mdiFilterVariant" :size="18" />
          Filtros
          <v-icon :icon="showMobileFilters ? mdiChevronUp : mdiChevronDown" :size="16" />
        </button>
      </div>
      <div v-if="isSearchByIdEnabled" class="relative mt-2">
        <div class="flex items-center w-full bg-white p-1 rounded-lg border h-[40px]">
          <div class="relative w-full h-full flex items-center">
            <input
              v-model="searchId"
              @keyup.enter="handleSearchById"
              type="number"
              placeholder="Buscar ID..."
              :disabled="isSearching"
              class="w-full pl-2 pr-8 py-0 bg-transparent focus:outline-none text-sm border-none ring-0 h-full appearance-none"
            />
            <button
              @click="handleSearchById"
              :disabled="isSearching"
              class="absolute right-0 top-0 bottom-0 px-2 text-gray-400 hover:text-primary transition-colors disabled:cursor-not-allowed flex items-center justify-center"
            >
              <v-icon v-if="!isSearching" :icon="mdiMagnify" size="20" class="text-gray-400" />
              <v-icon v-else :icon="mdiSync" size="20" class="animate-spin text-primary" />
            </button>
          </div>
        </div>
        <span
          v-if="searchError"
          class="text-red-500 text-[10px] font-medium animate-pulse px-1 absolute top-full left-0"
        >
          {{ searchError }}
        </span>
      </div>
      <div v-show="showMobileFilters" class="mt-2">
        <filters />
      </div>
    </div>

    <!-- Desktop filters -->
    <div class="hidden md:flex flex-row items-center gap-2 w-full">
      <div class="flex-grow w-full md:w-auto">
        <filters />
      </div>
      <div v-if="isSearchByIdEnabled" class="relative shrink-0">
        <div class="flex items-center w-[220px] bg-white p-1 rounded-lg border h-[40px]">
          <div class="relative w-full h-full flex items-center">
            <input
              v-model="searchId"
              @keyup.enter="handleSearchById"
              type="number"
              placeholder="Buscar ID..."
              :disabled="isSearching"
              class="w-full pl-2 pr-8 py-0 bg-transparent focus:outline-none text-sm border-none ring-0 h-full appearance-none"
            />
            <button
              @click="handleSearchById"
              :disabled="isSearching"
              class="absolute right-0 top-0 bottom-0 px-2 text-gray-400 hover:text-primary transition-colors disabled:cursor-not-allowed flex items-center justify-center"
            >
              <v-icon v-if="!isSearching" :icon="mdiMagnify" size="20" class="text-gray-400" />
              <v-icon v-else :icon="mdiLoading" size="20" class="animate-spin text-primary" />
            </button>
          </div>
        </div>
        <span
          v-if="searchError"
          class="text-red-500 text-[10px] font-medium animate-pulse px-1 absolute top-full left-0 whitespace-nowrap"
        >
          {{ searchError }}
        </span>
      </div>
    </div>

    <template v-if="isLoadingListStations">
      <DashboardSkeleton />
    </template>

    <!-- Falha no load ≠ grade vazia: decidir escala com base numa lista falsamente
         vazia é o risco central — erro ganha estado visível com retry. -->
    <template v-else-if="loadError">
      <ReportErrorState
        title="Não foi possível carregar os plantões"
        :message="loadErrorDetail"
        @retry="retryLoadStations"
      />
    </template>

    <template v-else>
      <DashboardView
        id="dashboard"
        :weekData="listStations"
        :week-table-head="listStations?.tableHead || []"
        :selectedYearDay="selectedYearDay"
        @openDutyModal="handleOpenDetailsModal"
        @update-date="handleUpdateDate"
      />
    </template>
  </div>

  <update-station-modal
    v-if="stationSelected && showEditDutyModal"
    :infos="stationSelected"
    :exception-status="stationSelected.exceptionStatus"
    :show-modal="showEditDutyModal"
    @closeModal="handleEditSaved"
    @goBack="handleGoBack"
    @saved="handleGoBack"
  />

  <create-station-modal
    :showModal="showCreateStationModal"
    @closeModal="showCreateStationModal = false"
  />
  <export-excel-modal />
  <export-stations-excel-modal />
  <export-stations-modal />

  <station-details-modal
    v-if="stationSelected"
    :infos="stationSelected"
    :showModal="showDetailsModal"
    :exception-id-selected="pendingExceptionId"
    @update:showModal="showDetailsModal = $event"
    @closeModal="handleShowDetailsModal"
    @editStation="handleEditStation"
    @navigateToStation="handleNavigateToStation"
  />
</template>

<script lang="ts">
import { storeToRefs } from 'pinia'
import {
  defineAsyncComponent,
  defineEmits,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  computed,
  watch
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { startOfWeek, endOfWeek } from 'date-fns'
import LegendsGroup from '@/components/LegendsGroup/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import DashboardView from './components/DashboardView.vue'
import DashboardSkeleton from '@/components/designSystem/Skeleton/DashboardSkeleton.vue'
// Mesmo padrão visual de estado de erro do piloto M1 (relatórios gerenciais).
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import UpdateStationModal from './modals/updateStation/update.station.modal.vue'
import CreateStationModal from './modals/createStation/create.station.modal.vue'
import { useStationStore, STATIONS_LOAD_ERROR } from '@/pinia-store/useStationStore'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import Filters from '@/components/Dashboard/Header/index.vue'
import {
  mdiDownloadOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiMicrosoftExcel,
  mdiFilterVariant,
  mdiLoading,
  mdiMagnify,
  mdiSync
} from '@mdi/js'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import ExportExcelModal from './modals/exportExcel/export.excel.modal.vue'
import ExportStationsModal from './modals/exportStations/export.stations.modal.vue'
import { StationToUpdate, mapCoverageToStationUpdate } from './modals/createStation/helpers'
import { ShiftDetails } from '@/Network/Types/Responses/StationResponses'
import { CompanyDoctorInviteStatusEnum } from '@/Network/Types/Requests/CompanyRequests'
import StationDetailsModal from './components/stationDetailsModal.vue'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import ExportStationsExcelModal from './modals/exportStationsExcel/export.stations.excel.modal.vue'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import NetworkService from '@/Network/index'
import { withNavigatedStationId } from '@/utils/permanence'
const excelIcon = defineAsyncComponent(() => import('@/assets/icons/excel.vue'))
const pdfIcon = defineAsyncComponent(() => import('@/assets/icons/pdf.vue'))

export default {
  name: 'Dashboard',
  components: {
    pdfIcon,
    excelIcon,
    LegendsGroup,
    OutlineButton,
    FilledButton,
    DashboardView,
    DashboardSkeleton,
    ReportErrorState,
    Filters,
    UpdateStationModal,
    CreateStationModal,
    ExportExcelModal,
    ExportStationsExcelModal,
    ExportStationsModal,
    StationDetailsModal
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const selectedYearDay = ref({ day: -1, month: -1 })
    const pendingExceptionId = ref<number | null>(null)
    const showEditDutyModal = ref(false)
    const showCreateStationModal = ref(false)
    const showDetailsModal = ref(false)
    const showExportMenu = ref(false)
    const showMobileFilters = ref(false)
    const exportMenuContainerDesktop = ref<HTMLElement | null>(null)
    const exportMenuContainerMobile = ref<HTMLElement | null>(null)
    const authStore = useAuthStore()
    const permissionsStore = usePermissionsStore()
    const searchId = ref('')
    const searchError = ref('')
    const isSearching = ref(false)
    const handleUpdateDate = async (date: Date) => {
      const startDate = startOfWeek(date, { weekStartsOn: 1 })
      const endDate = endOfWeek(date, { weekStartsOn: 1 })
      await stationStore.setDateFilter({ startDate, endDate })
    }
    function mapShiftDetailsToStationUpdate(res: ShiftDetails): StationToUpdate {
      return {
        stationId: res.id,
        companyId: res.schedule?.healthUnit?.company?.id ?? 0,
        doctorId: res.doctorStation?.doctor?.id,
        date: new Date(res.startHour),
        startHour: new Date(res.startHour),
        endHour: new Date(res.endHour),
        timezone: res.timezone,
        stationType: res.type,
        ourTeam: res.doctorStation?.ourTeam ?? false,
        value: res.value ?? 0,
        inCash: res.doctorStation?.inCash ?? false,
        advancePaid: res.doctorStation?.antecipeWasPaid ?? false,
        advanceRequested: res.doctorStation?.requestAntecipe ?? false,
        modifiedValue: false,
        doctorPending: false,
        urlConviteDoctor: '',
        observations: [],
        checks:
          res.doctorStation?.doctorChecks?.map((c: any) => ({
            ...c,
            checkin: String(c.checkin),
            checkout: c.checkout ? String(c.checkout) : undefined
          })) ?? [],
        position: {
          id: res.position?.id,
          name: res.position?.name
        },
        healthUnitLatitude: (res.schedule?.healthUnit as any)?.address?.latitude,
        healthUnitLongitude: (res.schedule?.healthUnit as any)?.address?.longitude,
        stationValue: res.value ?? 0,
        userSponsor: res.doctorStation?.userSponsor,
        scheduleId: res.schedule?.id,
        doctorNpsScore: res.doctorNpsScore ?? null,
        // F2 — campo aditivo de `GET /stations/:id`. Repassado só quando vier
        // como boolean: `undefined` é o sinal de "backend sem a F2", e é ele que
        // faz a edição do plantão cair no fallback por `registration-settings`.
        manualCheckBlockEnabled: res.manualCheckBlockEnabled,
        // E-06: estado da cobertura (vazio com BE antigo) — libera a edição sem médico.
        ...mapCoverageToStationUpdate(res)
      }
    }

    const handleSearchById = async () => {
      searchError.value = ''
      if (!searchId.value) return
      const targetId = Number(searchId.value)
      isSearching.value = true
      try {
        const res = await NetworkService.station.getStationDetails(targetId)
        if (res) {
          if (res.startHour) {
            handleUpdateDate(new Date(res.startHour))
          }
          handleOpenDetailsModal(mapShiftDetailsToStationUpdate(res))
        }
      } catch (error) {
        console.error(error)
        searchError.value = `Plantão #${targetId} não encontrado.`
      } finally {
        isSearching.value = false
      }
    }

    const canStationDoctorCheckXlsReportExporting = computed(() => {
      return permissionsStore.checkAnyRessources({
        permission: AuthorizationPermissions.StationDoctorCheckXlsReportExporting,
        resourceType: AuthorizationResourcesAndSubjects.Schedule
      })
    })

    const canStationPdfReportExporting = computed(() => {
      return permissionsStore.checkAnyRessources({
        permission: AuthorizationPermissions.StationPdfReportExporting,
        resourceType: AuthorizationResourcesAndSubjects.Schedule
      })
    })

    const canStationXlsReportExporting = computed(() => {
      return permissionsStore.checkAnyRessources({
        permission: AuthorizationPermissions.StationXlsReportExporting,
        resourceType: AuthorizationResourcesAndSubjects.Schedule
      })
    })

    const canCreateStation = computed(() => {
      return permissionsStore.checkAnyRessources({
        permission: AuthorizationPermissions.StationCreating,
        resourceType: AuthorizationResourcesAndSubjects.Schedule
      })
    })

    const stationSelected = ref<StationToUpdate>()

    defineEmits(['closeModal'])

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const insideDesktop = exportMenuContainerDesktop.value?.contains(target)
      const insideMobile = exportMenuContainerMobile.value?.contains(target)
      if (!insideDesktop && !insideMobile) {
        showExportMenu.value = false
      }
    }

    const stationStore = useStationStore()
    const isSearchByIdEnabled = ref(process.env.VUE_APP_ENABLED_SEARCH_BY_ID === 'true')
    onMounted(async () => {
      document.addEventListener('mousedown', handleClickOutside)

      const hasQueryFilters = Object.keys(route.query).length > 0

      if (hasQueryFilters) {
        const queryFromUrl: any = { ...route.query }

        const arrayFields = ['healthUnits', 'schedules', 'stationType', 'operationalGroups']
        arrayFields.forEach((field) => {
          if (queryFromUrl[field] && !Array.isArray(queryFromUrl[field])) {
            queryFromUrl[field] = [queryFromUrl[field]]
          }
        })

        const booleanFields = [
          'withCheckin',
          'withCheckout',
          'inCash',
          'advanceRequested',
          'advancePaid',
          'ourTeam',
          'openedStation'
        ]
        booleanFields.forEach((field) => {
          if (queryFromUrl[field] === 'true') queryFromUrl[field] = true
          if (queryFromUrl[field] === 'false') queryFromUrl[field] = false
        })

        if (!queryFromUrl.startHour) queryFromUrl.startHour = undefined
        if (!queryFromUrl.endHour) queryFromUrl.endHour = undefined

        stationStore.listStationsQuery = {
          ...stationStore.listStationsQuery,
          ...queryFromUrl
        }

        // Normalizar range de datas para semana seg-dom (corrige URLs antigas/bookmarks)
        if (stationStore.listStationsQuery.startDate) {
          const parsedStart = new Date(stationStore.listStationsQuery.startDate)
          if (!isNaN(parsedStart.getTime())) {
            const normalizedStart = startOfWeek(parsedStart, { weekStartsOn: 1 })
            const normalizedEnd = endOfWeek(parsedStart, { weekStartsOn: 1 })
            stationStore.listStationsQuery.startDate = normalizedStart.toISOString()
            stationStore.listStationsQuery.endDate = normalizedEnd.toISOString()
          }
        }
      } else if (stationStore.hasActiveFilters) {
        // Re-navegação: filtros estão no store — restaurar URL e re-buscar
        stationStore.setQueryString()
      } else {
        // Primeira carga ou estado limpo — resetar padrão
        await stationStore.setDefaultQuery()

        const now = new Date()
        const startDate = startOfWeek(now, { weekStartsOn: 1 })
        const endDate = endOfWeek(now, { weekStartsOn: 1 })

        await stationStore.setDateFilter({ startDate, endDate })
        return // setDateFilter já chama getListStations()
      }

      await stationStore.getListStations()

      if (route.query.stationId) {
        const stationId = Number(route.query.stationId)
        pendingExceptionId.value = route.query.exceptionId ? Number(route.query.exceptionId) : null
        stationSelected.value = { stationId } as StationToUpdate
        showDetailsModal.value = true
      }
    })

    watch(
      () => ({
        stationId: route.query.stationId,
        exceptionId: route.query.exceptionId
      }),
      (newQuery, oldQuery) => {
        if (
          newQuery.stationId &&
          (newQuery.stationId !== oldQuery?.stationId ||
            newQuery.exceptionId !== oldQuery?.exceptionId)
        ) {
          const stationId = Number(newQuery.stationId)
          pendingExceptionId.value = newQuery.exceptionId ? Number(newQuery.exceptionId) : null
          stationSelected.value = { stationId } as StationToUpdate
          showDetailsModal.value = true
        }
      }
    )

    onUnmounted(() => {
      document.removeEventListener('mousedown', handleClickOutside)
    })

    const modalStore = useModalStore()
    const toggleExportModal = () => {
      modalStore.toggleModal(EModals.EXPORT_DASHBOARD_EXCEL)
    }

    const toggleExportStationsModal = () => {
      modalStore.toggleModal(EModals.EXPORT_STATIONS_EXCEL)
    }

    const downloadSchedules = () => {
      modalStore.toggleModal(EModals.EXPORT_DASHBOARD_STATIONS)
      showExportMenu.value = false
    }

    const { listStations, isLoadingListStations, loadError } = storeToRefs(useStationStore())

    // Evita repetir o título quando a mensagem do store é o próprio fallback
    // genérico ("Não foi possível carregar os plantões").
    const loadErrorDetail = computed(() =>
      loadError.value && loadError.value !== STATIONS_LOAD_ERROR ? loadError.value : null
    )

    const retryLoadStations = async () => {
      await stationStore.getListStations()
    }

    const doctorStore = useDoctorStore()

    const handleOpenDetailsModal = async (data: StationToUpdate) => {
      const fullStation = stationStore.listStations?.data
        .flatMap((company) => company.healthUnities)
        .flatMap((unit) => unit.schedules)
        .flatMap((schedule) => schedule.shifts)
        .flatMap((shift) => shift.hours)
        .flatMap((hours) => hours.positions)
        .flatMap((position) => position.stations)
        .flatMap((dayList) => dayList)
        .find((station) => station.stationId === data.stationId)

      if (fullStation) {
        stationSelected.value = {
          ...data,
          doctorStationId: fullStation.doctorStationId ?? null
        }
      } else {
        stationSelected.value = data
      }

      showDetailsModal.value = true
    }

    const handleEditStation = async (fullStationData: any) => {
      if (!stationSelected.value) return
      const exceptionStatus = fullStationData.doctorStation?.exceptionSummaryStatus || null

      stationSelected.value = {
        ...stationSelected.value,
        stationId: fullStationData.id,
        companyId: fullStationData.schedule.healthUnit.company.id,
        doctorId: fullStationData?.doctorStation?.doctor?.id || undefined,
        date: fullStationData.date,
        startHour: fullStationData.startHour,
        endHour: fullStationData.endHour,
        timezone: fullStationData.timezone,
        stationType: fullStationData.doctorStation.type,
        ourTeam: fullStationData.doctorStation.ourTeam,
        advanceRequested: fullStationData.doctorStation.requestAntecipe,
        advancePaid: fullStationData.doctorStation.antecipeWasPaid,
        modifiedValue: fullStationData.doctorStation.value,
        value: fullStationData.value,
        checks: fullStationData.doctorStation.doctorChecks,
        stationValue: fullStationData.value,
        position: fullStationData.position,
        doctorStationId: fullStationData.doctorStation.id,
        schedule: fullStationData.schedule,
        doctorStation: fullStationData.doctorStation,
        shift: fullStationData.shift,
        exceptionStatus: exceptionStatus,
        // O detalhe do plantão é a resposta fresca de `GET /stations/:id`; é o
        // caminho que traz a flag quando o card veio do calendário (que não
        // passa pelo `mapShiftDetailsToStationUpdate`). Sem ela no payload, a
        // aba de checks segue no fallback por `registration-settings`.
        manualCheckBlockEnabled:
          typeof fullStationData.manualCheckBlockEnabled === 'boolean'
            ? fullStationData.manualCheckBlockEnabled
            : stationSelected.value.manualCheckBlockEnabled,
        // E-06: o detalhe fresco também traz o estado da cobertura (caminho do calendário).
        ...mapCoverageToStationUpdate(fullStationData)
      }

      await doctorStore.applyFilters(
        {
          search: '',
          names: [],
          crms: [],
          crmUFs: [],
          companies: [String(stationSelected.value.companyId)],
          inviteStatus: [
            CompanyDoctorInviteStatusEnum.ACCEPTED,
            CompanyDoctorInviteStatusEnum.PENDING
          ]
        },
        false
      )
      showDetailsModal.value = false
      showEditDutyModal.value = true
    }

    function handleEditSaved() {
      showEditDutyModal.value = false
      nextTick(() => {
        stationSelected.value = undefined
      })
    }

    function handleGoBack() {
      showEditDutyModal.value = false
      nextTick(() => {
        showDetailsModal.value = true
      })
    }

    function handleShowDetailsModal() {
      showCreateStationModal.value = false
      pendingExceptionId.value = null
      if (route.query.stationId || route.query.exceptionId) {
        const query = { ...route.query }
        delete query.stationId
        delete query.exceptionId
        delete query.openSidebar
        router.replace({ query })
      }
      nextTick(() => {
        stationSelected.value = undefined
      })
    }

    // Navegação real da linha de vínculo de Registro de Permanência: só troca o
    // `stationId` da seleção já existente — o `station-details-modal` (mantido
    // aberto) refaz o fetch sozinho via watch interno de `props.infos.stationId`.
    // `stationSelected` aqui é tipado como `StationToUpdate | undefined` (não
    // `| null`, ao contrário dos outros 2 consumidores) — guarda o resultado
    // de `withNavigatedStationId` (que pode ser `null`) em vez de atribuir
    // direto, evitando alargar o tipo do ref só por causa deste handler.
    function handleNavigateToStation(stationId: number) {
      const navigated = withNavigatedStationId(stationSelected.value, stationId)
      if (navigated) {
        stationSelected.value = navigated
      }
    }

    const toggleExportMenu = () => {
      showExportMenu.value = !showExportMenu.value
    }

    const downloadExcel = () => {
      toggleExportModal()
      showExportMenu.value = false
    }

    const downloadStationsExcel = () => {
      toggleExportStationsModal()
      showExportMenu.value = false
    }

    return {
      exportMenuContainerDesktop,
      exportMenuContainerMobile,
      EModals,
      modalStore,
      selectedYearDay,
      showEditDutyModal,
      showCreateStationModal,
      listStations,
      stationSelected,
      handleOpenDetailsModal,
      handleEditStation,
      mdiChevronDown,
      mdiSync,
      mdiChevronUp,
      mdiDownloadOutline,
      mdiMicrosoftExcel,
      mdiFilterVariant,
      showMobileFilters,
      mdiMagnify,
      mdiLoading,
      toggleExportModal,
      isLoadingListStations,
      loadError,
      loadErrorDetail,
      retryLoadStations,
      showDetailsModal,
      handleEditSaved,
      handleGoBack,
      handleShowDetailsModal,
      handleNavigateToStation,
      showExportMenu,
      toggleExportMenu,
      downloadExcel,
      downloadStationsExcel,
      downloadSchedules,
      authStore,
      canStationDoctorCheckXlsReportExporting,
      canStationPdfReportExporting,
      canStationXlsReportExporting,
      canCreateStation,
      searchId,
      searchError,
      isSearching,
      handleSearchById,
      handleUpdateDate,
      isSearchByIdEnabled,
      pendingExceptionId
    }
  }
}
</script>
<style scoped>
@media screen and (max-width: 760px) {
  .search-responsive-container {
    margin-top: 0 !important;
    width: 100% !important;
    position: static !important;
    margin-bottom: 16px;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .search-responsive-box {
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>
